package co.uk.paxes.pinpoint.model

import kotlin.random.Random

/**
 * Medium difficulty AI strategy
 * - Score-based move selection
 * - 10% chance of suboptimal moves for variety
 * - Basic joker usage for 5+ point opportunities
 */
class MediumStrategy(private val playerId: String) : AIStrategy {
    
    override fun selectMove(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Move? {
        val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
        if (emptyPositions.isEmpty() || availablePins.isEmpty()) {
            println("MediumStrategy: No empty positions or pins available")
            return null
        }
        
        println("MediumStrategy: Evaluating ${availablePins.size} pins × ${emptyPositions.size} positions")
        
        // Simple approach: Find the move with the best immediate score
        val scoreCalculator = ScoreCalculator()
        val scoredMoves = mutableListOf<Pair<Move, Int>>()
        
        try {
            // Evaluate normal moves
            for (pin in availablePins) {
                for (position in emptyPositions) {
                    val move = Move(playerId, pin, position)
                    val scoreResult = scoreCalculator.calculateMoveScore(gameState.board, move)
                    scoredMoves.add(Pair(move, scoreResult.points))
                }
            }
            
            // Evaluate joker moves if available
            val currentPlayer = gameState.currentPlayer
            if (currentPlayer.canUseJoker() && availablePins.isNotEmpty()) {
                println("MediumStrategy: 🃏 Evaluating joker moves")
                
                val bestNormalScore = scoredMoves.maxOfOrNull { it.second } ?: 0
                
                for (sacrificePin in availablePins) {
                    for (jokerColor in PinColor.values()) {
                        if (jokerColor == PinColor.BLACK) continue
                        if (jokerColor == sacrificePin.color) continue
                        
                        val jokerPin = Pin.joker(sacrificePin.color, jokerColor)
                        
                        // Find best position for this joker
                        var bestJokerScore = 0
                        var bestJokerPosition: Position? = null
                        for (position in emptyPositions) {
                            val jokerMove = Move(playerId, jokerPin, position)
                            val jokerScoreResult = scoreCalculator.calculateMoveScore(gameState.board, jokerMove)
                            if (jokerScoreResult.points > bestJokerScore) {
                                bestJokerScore = jokerScoreResult.points
                                bestJokerPosition = position
                            }
                        }
                        
                        // Use joker if it scores >= normal moves
                        if (bestJokerPosition != null && bestJokerScore >= bestNormalScore) {
                            val jokerMove = Move(playerId, jokerPin, bestJokerPosition)
                            // Add small bonus to prefer joker when equal
                            val jokerValue = bestJokerScore + (if (bestJokerScore >= bestNormalScore) 1 else 0)
                            scoredMoves.add(Pair(jokerMove, jokerValue))
                            println("MediumStrategy:   🃏 Joker: ${sacrificePin.color}→$jokerColor = $bestJokerScore points")
                        }
                    }
                }
            }
        } catch (e: Exception) {
            println("MediumStrategy: Error evaluating moves: ${e.message}")
            // Fallback to random move
            val randomPin = availablePins.random()
            val randomPosition = emptyPositions.random()
            return Move(playerId, randomPin, randomPosition)
        }
        
        if (scoredMoves.isEmpty()) {
            println("MediumStrategy: No moves generated, using random move")
            val randomPin = availablePins.random()
            val randomPosition = emptyPositions.random()
            return Move(playerId, randomPin, randomPosition)
        }
        
        // Sort by score descending
        val sortedMoves = scoredMoves.sortedByDescending { it.second }
        
        println("MediumStrategy: Best move scores: ${sortedMoves.take(3).map { it.second }}")
        
        // 10% chance to make a suboptimal move
        val shouldMakeSuboptimal = Random.nextFloat() < 0.1f
        
        if (shouldMakeSuboptimal && sortedMoves.size > 3) {
            // Pick from good moves but not the best
            val suboptimalMoves = sortedMoves.drop(1).take(sortedMoves.size / 3)
            return suboptimalMoves.randomOrNull()?.first
        }
        
        // Select the best move
        return sortedMoves.firstOrNull()?.first
    }
    
    override fun shouldCreateJoker(
        gameState: GameState,
        availablePins: List<Pin>,
        boardAnalyzer: BoardAnalyzer
    ): Boolean {
        // Check if creating a joker would enable a 5+ point move
        val scoringOpps = boardAnalyzer.findScoringOpportunities(gameState, availablePins)
        val bestNormalScore = scoringOpps.maxOfOrNull { it.potentialScore } ?: 0
        
        // If we can already score 5+ points, don't bother with joker
        if (bestNormalScore >= 5) {
            return false
        }
        
        // Check if a joker could score 5+ points
        // Simulate having a joker of each color
        for (color in PinColor.values()) {
            if (color == PinColor.BLACK) continue
            
            val emptyPositions = boardAnalyzer.getEmptyPositions(gameState.board)
            for (position in emptyPositions) {
                val score = boardAnalyzer.simulateScore(gameState.board, position, color)
                if (score >= 5) {
                    return true
                }
            }
        }
        
        return false
    }
    
    override fun selectJokerColor(
        gameState: GameState,
        boardAnalyzer: BoardAnalyzer
    ): PinColor? {
        // Select joker color based on board density
        val colorDensity = boardAnalyzer.analyzeColorDensity(gameState.board)
        
        // Prefer colors with higher density (more likely to form patterns)
        val bestColor = colorDensity
            .filter { it.key != PinColor.BLACK }
            .maxByOrNull { it.value }
            ?.key
        
        return bestColor
    }
}
